import React, { useState } from 'react';
import { images } from '../utils/assetHelpers';
import qrcodeImage from '../assets/images/qrcode.png';
import MaterialIcon from './MaterialIcon';

const ConsultationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    type: 'company',
    name: '',
    phone: '',
    company: '',
    service: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  // 服务类型映射，将value转换为用户看到的中文文本
  const getServiceText = (value) => {
    const serviceMap = {
      'founder': '创始人教练',
      'executive-workshop': '高管全景领导力工作坊',
      'coaching-leadership': '教练式领导力课程',
      'executive-coach': '高管领导力教练',
      'team-coach': '团队升维教练',
      'star-coach': '潜在明星激活教练'
    };
    return serviceMap[value] || value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 验证必填字段
    if (!formData.phone.trim()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // 准备提交数据
      const submitData = {
        type: formData.type === 'company' ? 2 : 1, // 1=个人, 2=企业
        name: formData.name ? formData.name.substring(0, 32) : null, // 最大32字符
        phone: formData.phone.substring(0, 32), // 必填，最大32字符
        company: formData.type === 'company' && formData.company ? formData.company.substring(0, 128) : null, // 最大128字符
        service: formData.type === 'company' && formData.service ? getServiceText(formData.service) : null
      };

      const response = await fetch('https://open.ga.api.betterus.cc/api/custom/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // 清空表单
        setFormData({
          type: 'company',
          name: '',
          phone: '',
          company: '',
          service: '',
        });
        // 3秒后关闭弹窗
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 3000);
      } else {
        throw new Error('提交失败');
      }
    } catch (error) {
      console.error('表单提交错误:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* 背景遮罩 */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      ></div>

      {/* 浮层内容 */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <div 
          className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
          style={{ maxHeight: '90vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* 移动端二维码区域 */}
          <div className="lg:hidden w-full bg-gradient-to-br from-amber-500 to-yellow-500 p-6 flex flex-col items-center justify-center text-white">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold mb-2">扫码联系顾问</h3>
              <p className="text-white/80">添加微信，获取专业咨询</p>
            </div>
            <div className="w-40 h-40 bg-white rounded-lg p-3">
              <img 
                src={qrcodeImage} 
                alt="微信二维码" 
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.parentElement.innerHTML = `
                    <div class="w-full h-full flex items-center justify-center text-amber-500 text-center p-2">
                      <div>
                        <MaterialIcon icon="qr_code_2" className="text-4xl mb-2" />
                        <p class="text-sm">扫码添加微信</p>
                      </div>
                    </div>
                  `;
                }}
              />
            </div>
          </div>

          <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
            {/* 桌面端二维码区域 */}
            <div className="hidden lg:flex lg:w-1/3 bg-gradient-to-br from-amber-500 to-yellow-500 p-8 flex-col items-center justify-center text-white">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold mb-2">扫码联系顾问</h3>
                <p className="text-white/80">获取专业咨询</p>
              </div>
              <div className="w-48 h-48 bg-white rounded-lg p-4">
                <img 
                  src={images.qrCodes.wechat} 
                  alt="微信二维码" 
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.parentElement.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center text-amber-500 text-center p-2">
                        <div>
                          <MaterialIcon icon="qr_code_2" className="text-4xl mb-2" />
                          <p class="text-sm">扫码添加微信</p>
                        </div>
                      </div>
                    `;
                  }}
                />
              </div>
            </div>

            {/* 表单区域 */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">联系顾问</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                >
                  <MaterialIcon icon="close" className="text-gray-500" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* 成功提示 */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
                    <MaterialIcon icon="check_circle" className="text-green-500" />
                    <div>
                      <p className="text-green-800 font-medium">提交成功！</p>
                      <p className="text-green-600 text-sm">我们的顾问将尽快与您联系</p>
                    </div>
                  </div>
                )}

                {/* 错误提示 */}
                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
                    <MaterialIcon icon="error" className="text-red-500" />
                    <div>
                      <p className="text-red-800 font-medium">提交失败</p>
                      <p className="text-red-600 text-sm">请检查网络连接或稍后重试</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">用户类型</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => handleChange({ target: { name: 'type', value: 'personal' } })}
                      className={`px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        formData.type === 'personal'
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-gray-300 hover:border-amber-300'
                      }`}
                    >
                      个人
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange({ target: { name: 'type', value: 'company' } })}
                      className={`px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        formData.type === 'company'
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-gray-300 hover:border-amber-300'
                      }`}
                    >
                      企业/组织
                    </button>
                  </div>
                </div>

                {formData.type === 'company' && (
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">公司/组织名称</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                      placeholder="请输入公司/组织名称"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">联系人</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                      placeholder="请输入联系人姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">联系电话 <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                      placeholder="请输入联系电话"
                      required
                    />
                  </div>
                </div>

                {formData.type === 'company' && (
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">意向服务</label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="">请选择意向服务</option>
                      <option value="founder">创始人教练</option>
                      <option value="executive-workshop">高管全景领导力工作坊</option>
                      <option value="coaching-leadership">教练式领导力课程</option>
                      <option value="executive-coach">高管领导力教练</option>
                      <option value="team-coach">团队升维教练</option>
                      <option value="star-coach">潜在明星激活教练</option>
                    </select>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || submitStatus === 'success'}
                  className={`w-full py-4 px-6 rounded-xl font-semibold transform transition-all duration-300 shadow-lg hover:shadow-xl ${
                    isSubmitting || submitStatus === 'success'
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:from-amber-600 hover:to-yellow-600 hover:-translate-y-1'
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>提交中...</span>
                    </div>
                  ) : submitStatus === 'success' ? (
                    <div className="flex items-center justify-center space-x-2">
                      <MaterialIcon icon="check" />
                      <span>提交成功</span>
                    </div>
                  ) : (
                    '请联系我'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationModal; 
